"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { QueryFilters, BaseService, PaginatedResponse } from "@cabukaka/types";
import { useToken } from "../auth/useToken";

export function useData<T>(
    service: BaseService<T>,
    filters?: QueryFilters,
) {
    const [data, setData] = useState<T[]>([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>(null);
    const { token } = useToken();
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);


    // Fonction pour changer de page
    const goToPage = (page: number) => {
        if (page >= 1 && page <= pages) {
            setCurrentPage(page);
        }
    };

    // Fonction pour la page suivante
    const nextPage = () => {
        if (currentPage < pages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Fonction pour la page précédente
    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Rafraîchir les données en restant sur la page courante
    const refreshCurrentPage = () => {
        refresh();
    };


    const serviceRef = useRef(service);

    useEffect(() => {
        serviceRef.current = service;
    }, [service]);

    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    const refresh = useCallback(async () => {

        setLoading(true);
        try {
            const res: PaginatedResponse<T> = await serviceRef.current.
                getAll(memoizedFilters, token!);


            setData(res.data);
            setTotal(res.total);
            setPages(res.pages);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [token, memoizedFilters]);

    const deleteItem = async (id: string) => {
        if (!token) {
            throw new Error("Une erreur s'est produite");
        }
        try {
            await serviceRef.current.delete(id, token);
            await refresh();
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const deleteManyItems = async (ids: string[]) => {
        if (!token) {
            throw new Error("Une erreur s'est produite");
        }
        try {
            await serviceRef.current.deleteMany(ids, token);
            await refresh();
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const updateItem = async (item: Partial<T> & { id?: any; _id?: any }) => {
        const id = item.id || item._id;
        if (!token || !id) {
            throw new Error("Une erreur s'est produite");
        }
        try {
            await serviceRef.current.update(id, item, token);
            await refresh();
        } catch (err) {

            setError(err);
            throw err
        }
    };

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        data,
        total,
        pages,
        loading,
        error,
        count: data.length,
        currentPage,
        limit,
        goToPage,
        nextPage,
        prevPage,
        refresh,
        refreshCurrentPage,
        deleteItem,
        updateItem,
        deleteManyItems
    };
}